import React, { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { InvitationData } from '../types';
import {
  fadeOpacities,
  progressForScene,
  sampleJourney,
} from './cameraPath';
import { resolvePlotPlaneContent } from './content';
import { resolvePlaneSources, type PlaneSource } from './planeSources';
import {
  JOURNEY_SCROLL_VH,
  elementCanScroll,
  findScrollParent,
  isEmbeddedPreview,
  progressFromOffsets,
  scrolledWithinParent,
  selfScrollProgress,
} from './layout';
import { webglAvailable } from './webgl';

const PlotPlaneCanvas = lazy(() => import('./PlotPlaneCanvas'));
const RsvpForm = lazy(() => import('@/src/modules/rsvp').then((mod) => ({ default: mod.RsvpForm })));

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(media.matches);
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, []);

  return reduced;
}

function readProgress(root: HTMLElement): number {
  const own = selfScrollProgress(root.scrollTop, root.scrollHeight, root.clientHeight);
  if (own !== null) return own;
  const parent = findScrollParent(root, (el) => getComputedStyle(el).overflowY);
  if (parent) {
    const scrolled = scrolledWithinParent(root.getBoundingClientRect().top, parent.getBoundingClientRect().top);
    return progressFromOffsets(scrolled, root.offsetHeight - parent.clientHeight);
  }
  return progressFromOffsets(-root.getBoundingClientRect().top, root.offsetHeight - window.innerHeight);
}

function scrollJourneyTo(root: HTMLElement, t: number) {
  if (elementCanScroll(root.scrollHeight, root.clientHeight)) {
    root.scrollTop = (root.scrollHeight - root.clientHeight) * t;
    return;
  }
  const parent = findScrollParent(root, (el) => getComputedStyle(el).overflowY);
  if (!parent) {
    const total = Math.max(0, root.offsetHeight - window.innerHeight);
    const top = window.scrollY + root.getBoundingClientRect().top + total * t;
    window.scrollTo(0, top);
    return;
  }
  const total = Math.max(0, root.offsetHeight - parent.clientHeight);
  const delta = root.getBoundingClientRect().top - parent.getBoundingClientRect().top;
  parent.scrollTop = parent.scrollTop + delta + total * t;
}

class WebglBoundary extends React.Component<
  { onFail: () => void; children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(): void {
    this.props.onFail();
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

function PlanePlate({ source, opacity, zIndex }: { source: PlaneSource; opacity: number; zIndex: number }) {
  const [src, setSrc] = useState(source.src);

  useEffect(() => {
    setSrc(source.src);
  }, [source.src]);

  return (
    <img
      src={src}
      alt=""
      data-plane={source.id}
      onError={() => {
        setSrc((current) => (current === source.fallbackSrc ? current : source.fallbackSrc));
      }}
      className="absolute inset-0 h-full w-full object-contain transition-opacity duration-700 motion-reduce:duration-500"
      style={{ opacity, zIndex }}
    />
  );
}

export function WeddingPlotPlaneExperience({ data }: { data: InvitationData }) {
  const content = useMemo(() => resolvePlotPlaneContent(data), [data]);
  const planes = useMemo(() => resolvePlaneSources(data), [data]);
  const samplePlateCount = planes.filter((plane) => !plane.fromHost).length;
  const showSampleNote =
    samplePlateCount > 0 && !(samplePlateCount === planes.length && data.id === 'demo-wedding-plot-plane');
  const shellRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef(0);
  const invalidateRef = useRef<() => void>(() => {});
  const [progress, setProgress] = useState(0);
  const [embedded, setEmbedded] = useState(false);
  const [webglReady, setWebglReady] = useState(false);
  const [webglBlocked, setWebglBlocked] = useState(false);
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const useWebgl = !reducedMotion && !webglBlocked;

  useLayoutEffect(() => {
    setEmbedded(isEmbeddedPreview(shellRef.current));
  }, []);

  useEffect(() => {
    if (useWebgl && !webglAvailable()) setWebglBlocked(true);
  }, [useWebgl]);

  const onReady = useCallback(() => setWebglReady(true), []);
  const onWebglFail = useCallback(() => setWebglBlocked(true), []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    let frame = 0;
    const measure = () => {
      const next = readProgress(root);
      progressRef.current = next;
      setProgress((prev) => (Math.abs(prev - next) < 0.0008 ? prev : next));
      invalidateRef.current();
    };
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    const parent = findScrollParent(root, (el) => getComputedStyle(el).overflowY);
    root.addEventListener('scroll', onScroll, { passive: true });
    parent?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(frame);
      root.removeEventListener('scroll', onScroll);
      parent?.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [embedded]);

  const aspect = typeof window === 'undefined' ? 9 / 16 : window.innerWidth / Math.max(1, window.innerHeight);
  const sample = sampleJourney(progress, aspect);
  const opacities = fadeOpacities(progress);
  const chapter = content.chapters[sample.sceneIndex];
  const isPreviewSample = String(data.id ?? '').startsWith('preview-');
  const isLive = !isPreviewSample && (data.status === 'PUBLISHED' || data.status === 'PROCESSING');
  const showFade = !useWebgl || !webglReady;

  const goToScene = (index: number) => {
    const root = rootRef.current;
    if (!root) return;
    scrollJourneyTo(root, progressForScene(index));
  };

  return (
    <div ref={shellRef} className="bg-[#140a07] text-white">
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;1,500&family=Outfit:wght@400;500;600&display=swap"
      />
      <div
        ref={rootRef}
        className={embedded ? 'h-[70vh] overflow-y-auto' : undefined}
        data-testid="plot-plane-scroller"
      >
        <div style={{ height: `${JOURNEY_SCROLL_VH}vh` }} data-testid="plot-plane-journey">
          <div className={`sticky top-0 overflow-hidden ${embedded ? 'h-[70vh]' : 'h-[100dvh]'}`}>
            <div
              className="absolute inset-0"
              style={{
                background:
                  'radial-gradient(ellipse at 50% 42%, #6b3418 0%, #2a120c 42%, #140a07 78%)',
              }}
            />

            {showFade &&
              planes.map((plane, index) => (
                <PlanePlate key={plane.id} source={plane} opacity={opacities[index] ?? 0} zIndex={index + 1} />
              ))}

            {useWebgl && (
              <WebglBoundary onFail={onWebglFail}>
                <Suspense fallback={null}>
                  <PlotPlaneCanvas
                    progressRef={progressRef}
                    invalidateRef={invalidateRef}
                    onReady={onReady}
                    sources={planes}
                  />
                </Suspense>
              </WebglBoundary>
            )}

            <div
              className="pointer-events-none absolute inset-0 z-20"
              style={{
                background:
                  'radial-gradient(circle at 72% 28%, rgba(255,214,140,0.55), rgba(255,120,40,0.18) 28%, transparent 58%)',
                opacity: sample.glow * 0.85,
                mixBlendMode: 'screen',
              }}
              aria-hidden
            />

            {content.photos.length > 0 && (
              <div className="absolute left-3 top-[max(0.75rem,env(safe-area-inset-top))] z-30 flex gap-3 sm:left-5">
                {content.photos.map((photo) => (
                  <figure key={photo.label} className="w-14 text-center">
                    <img
                      src={photo.src}
                      alt={photo.label}
                      className="h-14 w-14 rounded-full border-2 border-[#e8c872] object-cover shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
                    />
                    <figcaption className="mt-1 font-[Outfit,system-ui,sans-serif] text-[10px] uppercase tracking-[0.16em] text-amber-100">
                      {photo.label}
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}

            <div className="pointer-events-none absolute inset-x-0 top-[max(0.75rem,env(safe-area-inset-top))] z-30 flex justify-center px-4">
              <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-black/30 px-3 py-2 backdrop-blur-md" role="tablist" aria-label="Wedding scenes">
                {content.chapters.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-label={item.id === 'hands' ? 'Mehndi hands' : item.title}
                    aria-selected={index === sample.sceneIndex}
                    onClick={() => goToScene(index)}
                    className="flex h-11 w-8 items-center justify-center"
                  >
                    <span
                      className={`h-2.5 rounded-full transition-all ${
                        index === sample.sceneIndex ? 'w-6 bg-[#e8c872]' : 'w-2.5 bg-white/55'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {progress < 0.03 && (
              <p className="pointer-events-none absolute inset-x-0 top-16 z-30 text-center font-[Outfit,system-ui,sans-serif] text-xs uppercase tracking-[0.28em] text-amber-50/90">
                Scroll to enter
              </p>
            )}

            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 bg-gradient-to-t from-[#140a07]/80 via-[#140a07]/35 to-transparent px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-16">
              <div className="pointer-events-auto mx-auto w-full max-w-md rounded-3xl border border-white/20 bg-[#1a0c08]/60 px-5 py-4 shadow-[0_16px_50px_rgba(0,0,0,0.35)] backdrop-blur-md">
                <p className="font-[Outfit,system-ui,sans-serif] text-[11px] uppercase tracking-[0.22em] text-amber-100/80">
                  {content.hostLine}
                </p>
                <h1 className="mt-1 font-['Cormorant_Garamond',Georgia,serif] text-[2.35rem] leading-none text-[#fff8ee] sm:text-5xl">
                  {content.coupleLine}
                </h1>
                <p className="mt-2 font-['Cormorant_Garamond',Georgia,serif] text-2xl text-[#f3d48a]" aria-live="polite">
                  {chapter.title}
                </p>
                <p className="mt-1 font-[Outfit,system-ui,sans-serif] text-sm text-white/85">{chapter.line}</p>
                <p className="mt-3 font-[Outfit,system-ui,sans-serif] text-sm font-medium text-white">{content.dateLine}</p>
                <p className="font-[Outfit,system-ui,sans-serif] text-sm text-amber-50/90">{content.venueLine}</p>
                {chapter.detail && (
                  <p className="mt-1 font-[Outfit,system-ui,sans-serif] text-xs uppercase tracking-[0.14em] text-amber-100/75">
                    {chapter.detail}
                  </p>
                )}
                {showSampleNote && (
                  <p className="mt-2 font-[Outfit,system-ui,sans-serif] text-xs text-amber-100/75">
                    Scenes without your photos still show the sample plates.
                  </p>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setRsvpOpen(true)}
                    className="rounded-full bg-[#e8c872] px-5 py-2.5 font-[Outfit,system-ui,sans-serif] text-sm font-semibold text-[#2a160c]"
                  >
                    RSVP
                  </button>
                  {content.mapsQuery && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(content.mapsQuery)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-full border border-white/30 px-4 py-2.5 font-[Outfit,system-ui,sans-serif] text-sm text-white"
                    >
                      Directions
                    </a>
                  )}
                  {content.videoUrl && (
                    <button
                      type="button"
                      onClick={() => setVideoOpen(true)}
                      className="rounded-full border border-white/30 px-4 py-2.5 font-[Outfit,system-ui,sans-serif] text-sm text-white"
                    >
                      Our story
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section data-section-type="plot-plane-close" className="relative z-20 min-h-24 bg-[#140a07]" aria-label="Invitation close" />

      {rsvpOpen && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 p-4 sm:items-center" role="dialog" aria-modal="true" aria-label="RSVP">
          <div className="w-full max-w-md rounded-3xl bg-[#2a120c] p-5 text-white shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-['Cormorant_Garamond',Georgia,serif] text-3xl">RSVP</h2>
              <button type="button" onClick={() => setRsvpOpen(false)} className="text-sm text-amber-100">
                Close
              </button>
            </div>
            {!isLive && (
              <p className="mb-3 font-[Outfit,system-ui,sans-serif] text-xs text-amber-100/80">
                Preview only. Replies are saved once this invitation is published.
              </p>
            )}
            <Suspense fallback={<p className="text-sm text-white/70">Opening RSVP…</p>}>
              <RsvpForm invitationId={data.invitationId ?? data.id ?? null} isLive={isLive} externalLink={data.rsvpLink} />
            </Suspense>
          </div>
        </div>
      )}

      {videoOpen && content.videoUrl && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 p-4 sm:items-center" role="dialog" aria-modal="true" aria-label="Our Pre-Wedding Story & Special Memories">
          <div className="w-full max-w-2xl rounded-3xl bg-[#1a0c08] p-5 text-white">
            <div className="mb-3 flex items-start justify-between gap-4">
              <h2 className="font-['Cormorant_Garamond',Georgia,serif] text-3xl leading-tight">
                Our Pre-Wedding Story & Special Memories
              </h2>
              <button type="button" onClick={() => setVideoOpen(false)} className="text-sm text-amber-100">
                Close
              </button>
            </div>
            <video src={content.videoUrl} controls playsInline className="aspect-video w-full rounded-2xl bg-black" />
          </div>
        </div>
      )}
    </div>
  );
}
